import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/audit';

const ACTION_COLORS = { create: '#27ae60', update: '#2980b9', delete: '#e74c3c' };
const ACTION_LABELS = { create: 'CREATE', update: 'UPDATE', delete: 'DELETE' };

const TABLE_LABELS = {
  members: 'Members', loans: 'Loans', fines: 'Fines', treasury_transactions: 'Treasury',
  expenses: 'Expenses', rosca_cycles: 'ROSCA Cycles', rosca_registrations: 'ROSCA Registrations',
  rosca_contributions: 'ROSCA Contributions', rosca_slots: 'ROSCA Payouts',
  rosca_swaps: 'ROSCA Swaps', rosca_transfers: 'ROSCA Transfers',
  mifl: 'MIFL', savings: 'Savings', meetings: 'Meetings', resolutions: 'Resolutions',
  leadership: 'Leadership', correspondence: 'Correspondence', subscriptions: 'Subscriptions',
  users: 'Users', loan_payments: 'Loan Payments', meeting_attendance: 'Attendance',
  activities: 'Activities', activity_attendance: 'Activity Attendance'
};

function AuditLog() {
  const [entries, setEntries]   = useState([]);
  const [stats, setStats]       = useState(null);
  const [tables, setTables]     = useState([]);
  const [loading, setLoading]   = useState(false);
  const [expanded, setExpanded] = useState(null);

  const [filterTable,  setFilterTable]  = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterUser,   setFilterUser]   = useState('');
  const [filterFrom,   setFilterFrom]   = useState('');
  const [filterTo,     setFilterTo]     = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchStats();
    fetchTables();
    fetchEntries();
  }, []);

  const fetchEntries = (overrides = {}) => {
    setLoading(true);
    const params = {};
    const t  = overrides.table  ?? filterTable;
    const a  = overrides.action ?? filterAction;
    const u  = overrides.user   ?? filterUser;
    const fr = overrides.from   ?? filterFrom;
    const to = overrides.to     ?? filterTo;
    if (t)  params.table_name  = t;
    if (a)  params.action      = a;
    if (u)  params.changed_by  = u;
    if (fr) params.from_date   = fr;
    if (to) params.to_date     = to;
    axios.get(API, { headers, params })
      .then(r => setEntries(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const fetchStats = () => {
    axios.get(`${API}/stats`, { headers }).then(r => setStats(r.data)).catch(() => {});
  };

  const fetchTables = () => {
    axios.get(`${API}/tables`, { headers }).then(r => setTables(r.data)).catch(() => {});
  };

  const applyFilters  = () => fetchEntries();
  const clearFilters  = () => {
    setFilterTable(''); setFilterAction(''); setFilterUser('');
    setFilterFrom(''); setFilterTo('');
    fetchEntries({ table: '', action: '', user: '', from: '', to: '' });
  };

  const formatValue = (val) => {
    if (!val) return '—';
    if (val.length > 80) return val.substring(0, 80) + '...';
    return val;
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-UG', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const inp = { padding: '7px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' };
  const btn = (bg, fg='white') => ({
    background: bg, color: fg, border: 'none', padding: '7px 14px',
    borderRadius: '4px', cursor: 'pointer', fontSize: '13px'
  });

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: '#2c3e50' }}>Audit Log</h2>
        <p style={{ color: '#888', fontSize: '13px', margin: '4px 0 0' }}>
          Immutable record of every change across all modules — who, what, when
        </p>
      </div>

      {/* Stats KPIs */}
      {stats && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {[
            { label: 'Total Entries', value: Number(stats.total_entries).toLocaleString(), color: '#2c3e50' },
            { label: 'Creates', value: Number(stats.creates).toLocaleString(), color: '#27ae60' },
            { label: 'Updates', value: Number(stats.updates).toLocaleString(), color: '#2980b9' },
            { label: 'Deletes', value: Number(stats.deletes).toLocaleString(), color: '#e74c3c' },
            { label: 'Modules Tracked', value: stats.tables_tracked, color: '#8e44ad' },
            { label: 'Last Entry', value: stats.last_entry ? new Date(stats.last_entry).toLocaleDateString('en-UG') : '—', color: '#e67e22' },
          ].map((k, i) => (
            <div key={i} style={{ background: k.color, color: 'white', padding: '12px 16px', borderRadius: '8px', minWidth: '130px' }}>
              <div style={{ fontSize: '10px', opacity: 0.85 }}>{k.label}</div>
              <div style={{ fontSize: '18px', fontWeight: '700' }}>{k.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ background: '#f9f9f9', padding: '14px 16px', borderRadius: '8px', marginBottom: '14px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '180px' }}>
          <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d' }}>Module</label>
          <select style={inp} value={filterTable} onChange={e => setFilterTable(e.target.value)}>
            <option value=''>All Modules</option>
            {tables.map(t => <option key={t} value={t}>{TABLE_LABELS[t] || t}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d' }}>Action</label>
          <select style={inp} value={filterAction} onChange={e => setFilterAction(e.target.value)}>
            <option value=''>All Actions</option>
            <option value='create'>Create</option>
            <option value='update'>Update</option>
            <option value='delete'>Delete</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d' }}>From Date</label>
          <input type='date' style={inp} value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d' }}>To Date</label>
          <input type='date' style={inp} value={filterTo} onChange={e => setFilterTo(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={applyFilters} style={btn('#2c3e50')}>Filter</button>
          <button onClick={clearFilters} style={btn('#95a5a6')}>Clear</button>
        </div>
        <div style={{ marginLeft: 'auto', alignSelf: 'center' }}>
          <span style={{ background: '#ecf0f1', padding: '7px 14px', borderRadius: '6px', fontSize: '12px', color: '#666' }}>
            {loading ? 'Loading...' : `${entries.length} records`}
          </span>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {Object.entries(ACTION_COLORS).map(([action, color]) => (
          <div key={action} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
            <span style={{ background: color, color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>
              {ACTION_LABELS[action]}
            </span>
            <span style={{ color: '#666' }}>
              {action === 'create' ? 'New record added' : action === 'update' ? 'Record modified' : 'Record removed'}
            </span>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: '#2c3e50', color: 'white' }}>
              {['#', 'Date & Time', 'Module', 'Action', 'Record ID', 'Field Changed', 'Old Value', 'New Value', 'Changed By'].map(h => (
                <th key={h} style={{ padding: '10px', textAlign: 'left', fontSize: '12px', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && !loading && (
              <tr>
                <td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#aaa' }}>
                  No audit entries found — entries are recorded as data changes occur
                </td>
              </tr>
            )}
            {entries.map((e, i) => (
              <tr
                key={e.id}
                onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                style={{
                  background: expanded === e.id ? '#fffde7' :
                    e.action === 'delete' ? '#fdf3f3' :
                    e.action === 'create' ? '#f0faf4' :
                    i % 2 === 0 ? '#f9f9f9' : 'white',
                  borderBottom: '1px solid #eee',
                  cursor: 'pointer',
                  borderLeft: `4px solid ${ACTION_COLORS[e.action] || '#95a5a6'}`
                }}
              >
                <td style={{ padding: '10px', color: '#aaa', fontSize: '11px' }}>{e.id}</td>
                <td style={{ padding: '10px', whiteSpace: 'nowrap', fontSize: '12px' }}>
                  {formatDate(e.changed_at)}
                </td>
                <td style={{ padding: '10px' }}>
                  <span style={{ background: '#ecf0f1', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', color: '#2c3e50' }}>
                    {TABLE_LABELS[e.table_name] || e.table_name}
                  </span>
                </td>
                <td style={{ padding: '10px' }}>
                  <span style={{ background: ACTION_COLORS[e.action] || '#95a5a6', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>
                    {ACTION_LABELS[e.action] || e.action}
                  </span>
                </td>
                <td style={{ padding: '10px', fontFamily: 'monospace', fontSize: '12px', color: '#666' }}>
                  #{e.record_id}
                </td>
                <td style={{ padding: '10px', fontWeight: '600', color: '#2c3e50' }}>
                  {e.field_name || '—'}
                </td>
                <td style={{ padding: '10px', color: '#e74c3c', fontFamily: 'monospace', fontSize: '12px', maxWidth: '180px' }}>
                  {expanded === e.id ? (e.old_value || '—') : formatValue(e.old_value)}
                </td>
                <td style={{ padding: '10px', color: '#27ae60', fontFamily: 'monospace', fontSize: '12px', maxWidth: '180px' }}>
                  {expanded === e.id ? (e.new_value || '—') : formatValue(e.new_value)}
                </td>
                <td style={{ padding: '10px' }}>
                  <span style={{ background: '#2c3e50', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '11px' }}>
                    {e.changed_by_username || 'system'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {entries.length === 500 && (
        <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fef9e7', border: '1px solid #f39c12', borderRadius: '6px', fontSize: '12px', color: '#7d6608' }}>
          Showing latest 500 entries. Use filters to narrow down results.
        </div>
      )}

      {/* Note */}
      <div style={{ marginTop: '16px', padding: '10px 14px', background: '#eaf4fb', borderLeft: '4px solid #2980b9', borderRadius: '4px', fontSize: '12px', color: '#555' }}>
        <strong>Audit Log:</strong> Every record is immutable — entries cannot be edited or deleted. This log is prima facie evidence of all system activity. Click any row to expand full field values.
      </div>
    </div>
  );
}

export default AuditLog;